import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import React, { useContext, useEffect, useState } from 'react'
import { modalStyle } from '../../../partials/modalStyle'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'

const AddVehicle = ({
  open,
  setOpen,
  setSuccess,
  existingVehicle,
  onClose,
  loadVehicle,
}) => {
  const { setToast, setUpgradeRequired } = useContext(GlobalContext)
  const [callSign, setCallSign] = useState(existingVehicle?.callSign ?? '')
  const [numPlate, setNumPlate] = useState(existingVehicle?.regPlate ?? '')
  const [vehicleType, setVehicleType] = useState(
    existingVehicle?.vehicleType ?? '',
  )
  const [makeReadyLoadListOptions, setMakeReadyLoadListOptions] = useState([])
  const [makeReadyLoadList, setMakeReadyLoadList] = useState(
    existingVehicle?.makeReadyLoadList ?? null,
  )
  const [callsignError, setCallSignError] = useState(false)
  const [numPlateError, setNumPlateError] = useState(false)
  const [image, setImage] = useState()
  const [failed, setFailed] = useState(false)
  const [tooBig, setTooBig] = useState(false)
  const [vehicleTypes, setVehicleTypes] = useState([])
  const [baseLocation, setBaseLocation] = useState(
    existingVehicle?.baseLocation ?? null,
  )
  const [radioInfo, setRadioInfo] = useState(existingVehicle?.radioInfo ?? '')
  const [tyreSize, setTyreSize] = useState(existingVehicle?.tyreSize ?? '')
  const [duplicateCallsignError, setDuplicateCallsignError] = useState(false)

  const { baseLocationOptions } = useContext(GlobalContext)
  useEffect(() => {
    API.get('/settings?select=vehicleTypes%20baseLocations').then((res) => {
      if (res.status === 200) {
        setVehicleTypes(res.data.vehicleTypes ?? [])
      }
    })
  }, [])

  const fetchMakeReadyLoadListOptions = () => {
    API.get('/settings/makereadyloadlists?types=vehicles').then((res) => {
      if (res.status === 200) {
        setMakeReadyLoadListOptions(res.data)
      }
    })
  }

  useEffect(() => {
    fetchMakeReadyLoadListOptions()
  }, [])

  // Vehicle allowance is checked up front so the user gets the upgrade /
  // Enterprise+ popup rather than filling in a form that will be
  // refused. The server enforces the same limit on POST /newVehicle, so a
  // race here still ends in the popup via the global 403 handler.
  useEffect(() => {
    if (!open || existingVehicle) return
    API.get('/subscription/usage').then((res) => {
      if (res.status !== 200) return
      const vehicles = res.data?.vehicles
      if (vehicles?.limit > 0 && vehicles.used >= vehicles.limit) {
        setOpen ? setOpen(false) : onClose && onClose()
        setUpgradeRequired({
          limitReached: true,
          limitType: 'vehicles',
          used: vehicles.used,
          limit: vehicles.limit,
          currentTier: res.data?.subscriptionTier,
          goBack: true,
        })
      }
    })
  }, [open])

  const handleSubmit = async (e) => {
    e.preventDefault()
    !callSign ? setCallSignError(true) : setCallSignError(false)
    !numPlate ? setNumPlateError(true) : setNumPlateError(false)

    let imageKey = ''

    if (existingVehicle) {
      API.put('/edit-vehicle?vehicle=' + existingVehicle._id, {
        callSign,
        regPlate: numPlate,
        vehicleType,
        makeReadyLoadList: makeReadyLoadList?._id ?? null,
        baseLocation: baseLocation?._id ?? null,
        radioInfo,
        tyreSize,
      }).then((res) => {
        if (res.status === 200) {
          onClose && onClose()
          setToast({ message: 'Vehicle Updated' })
          loadVehicle()
        } else if (
          res.status === 400 &&
          res?.data?.e?.code === 11000 &&
          res?.data?.e?.errorResponse?.keyPattern?.callSign
        ) {
          setDuplicateCallsignError(true)
        }
      })
    } else {
      await API.post('/newVehicle', {
        callSign,
        regPlate: numPlate,
        image: imageKey,
        vehicleType,
        makeReadyLoadList: makeReadyLoadList?._id ?? null,
        baseLocation: baseLocation?._id ?? null,
        // TODO: All new tenants require at least one ('Default') base location
      })
        .then((res) => {
          if (res.data?.error === 'CallSignDuplicate') {
            setDuplicateCallsignError(true)
          } else if (res.status === 200) {
            setOpen(false)
            setSuccess(res.data)
          }
        })
        .catch((e) => {
          setFailed(true)
          console.log(e)
        })
    }

    if (image) {
      // TODO: Should this be done on the backend instead of sharing URL to frontend?
      await API.post('gets3uploadurl', {
        folder: 'vehicles/cardPhotos',
        extension: image[0].type.split('/')[1],
      })
        .then((res) => {
          imageKey = res.data.key
          API.put(res.data.url, image[0], {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        })
        .catch((e) => console.log(e))
    }
  }
  return (
    <Dialog
      open={open}
      onClose={setOpen ? () => setOpen(false) : onClose}
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {existingVehicle
          ? `Edit ${existingVehicle?.callSign}`
          : 'Add a new Vehicle'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          key={existingVehicle?._id || 'new'}
          gap={2}
          display="flex"
          flexDirection="column"
          component="form"
          onSubmit={handleSubmit}
        >
          <TextField
            value={callSign}
            onChange={(e) => setCallSign(e.target.value)}
            label="Call Sign"
            fullWidth
            required
            autoComplete="off"
            error={callsignError || duplicateCallsignError}
            helperText={
              duplicateCallsignError && 'This Call Sign already exists'
            }
          />
          <TextField
            value={numPlate}
            onChange={(e) => setNumPlate(e.target.value)}
            label="Number Plate"
            fullWidth
            required
            autoComplete="off"
            error={numPlateError}
          />
          <FormControl fullWidth required>
            <InputLabel>Vehicle Type</InputLabel>
            <Select
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value)}
              label="Vehicle Type"
            >
              {vehicleTypes.map((vehicle) => (
                <MenuItem key={vehicle} value={vehicle}>
                  {vehicle}
                </MenuItem>
              ))}
            </Select>
            {vehicleTypes.length === 0 && (
              <FormHelperText>
                No vehicle types configured yet — add them in Site Configuration
                → Fleet → Vehicles first.
              </FormHelperText>
            )}
          </FormControl>
          <Autocomplete
            options={baseLocationOptions}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            value={baseLocation}
            required
            onChange={(e, newValue) => setBaseLocation(newValue)}
            getOptionKey={(o) => o._id}
            getOptionLabel={(o) => o.name}
            renderInput={(params) => (
              <TextField {...params} label="Base Location" required />
            )}
          />
          <TextField
            fullWidth
            value={tyreSize}
            onChange={(e) => setTyreSize(e.target.value)}
            label="Tyre Size"
          />
          <TextField
            fullWidth
            value={radioInfo}
            onChange={(e) => setRadioInfo(e.target.value)}
            label="Radio Info"
          />
          <Stack direction="row" gap={1} alignItems="flex-start">
            <Autocomplete
              fullWidth
              value={makeReadyLoadList}
              onChange={(e, newValue) => setMakeReadyLoadList(newValue)}
              options={makeReadyLoadListOptions}
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(opt, val) => opt._id === val?._id}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Make Ready Load List" />
              )}
            />
            <Tooltip title="Refresh list">
              <IconButton
                onClick={fetchMakeReadyLoadListOptions}
                sx={{ mt: 1 }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={() =>
              window.open('/make-ready-load-lists/new?type=Vehicle', '_blank')
            }
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            Don&apos;t see the right list? Create one{' '}
            <OpenInNewIcon fontSize="inherit" />
          </Link>
          {!existingVehicle && (
            <TextField
              type="file"
              size="small"
              defaultValue={image}
              inputProps={{ accept: 'image/*' }}
              onChange={(e) => {
                const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
                if (e.target.files[0]?.size >= MAX_FILE_SIZE) {
                  setTooBig(true)
                } else {
                  setTooBig(false)
                  setImage(e.target.files)
                }
              }}
              label="Image"
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
          <Box display="flex" flexDirection="column" gap={1}>
            {failed && (
              <Alert severity="error">
                There was an error adding this vehicle. Please try again later.
              </Alert>
            )}
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button
                color="inherit"
                onClick={setOpen ? () => setOpen(false) : onClose}
              >
                Cancel
              </Button>
              <Button variant="contained" type="submit" disabled={failed}>
                {existingVehicle ? 'Save Changes' : 'Add Vehicle'}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default AddVehicle
